
class ProductVariants
extends CustomHTMLElement {

    async connectedCallback (){

        this.masterSelector = document
            .getElementById(this.getAttribute('form-id')).id

        if( ! this.masterSelector ){
            console.warn(`The variant selector for product with handle ${ this.productHandle } is not linked to any product form.`)
            return
        }

        this.optionSelectors = Array
            .from(this.querySelectorAll(`[ data-selector-type ]`))


        this.product = await ProductLoader.load(this.productHandle)

        this.delegate.on(
            'change' ,
            `[ name ^= option ]` ,
            this._onOptionChanged.bind(this)
        )

        this.masterSelector.addEventListener(
            'change' ,
            this._onMasterSelectorChanged.bind(this)
        )

        this._updateDisableSelectors()
        this.selectVariant(this.selectedVariant.id)
    }

    get selectedVariant (){
        return this._getVariantById(parseInt(this.masterSelector.value))
    }

    get productHandle (){
        return this.getAttribute('handle')
    }

    get hideSoldOutVariants (){
        return this.hasAttribute('hide-sold-out-variants')
    }

    get updateUrl (){
        return this.hasAttribute('update-url')
    }


    selectVariant ( id ){

        if( ! this._isVariantSelectable(this._getVariantById(id)) )
            id = this._getFirstMatchingAvailableOrSelectableVariant().id

        if( this.selectedVariant?.id === id )
            return

        this.masterSelector.value = id

        const event = new Event('change',{ bubbles : true })

        this.masterSelector.dispatchEvent(event)

        if( this.updateUrl && history.replaceState ){

            const newUrl = new URL(window.location)

            if( id )
                newUrl.searchParams.set('variant',id)
            else
                newUrl.searchParams.delete('variant')

            window.history.replaceState({
                path : newUrl.toString()
            },'',newUrl.toString())
        }

        this._updateDisableSelectors()

        triggerEvent(
            this.masterSelector.form ,
            'Variant:Changed' ,
            { variant : this.selectedVariant }
        )
    }

    _onOptionChanged (){
        this.selectVariant(this._getVariantFromOptions()?.id)
    }

    _onMasterSelectorChanged (){

        const options = this.selectedVariant?.options ?? []

        options.forEach(( value , index ) => {

            const input = this.querySelector(`
                input[ name = 'option${ index + 1 }' ][ value = '${ CSS.escape(value) }' ] ,
                select[ name = 'option${ index + 1 }' ]
            `)

            let triggerChangeEvent = false

            if( input.tagName === 'SELECT' ){
                triggerChangeEvent = ( input.value !== value )
                input.value = value
            } else
            if( input.tagName === 'INPUT' ){
                triggerChangeEvent = ( ! input.checked && input.value === value )
                input.checked = input.value === value
            }

            if( triggerChangeEvent )
                input.dispatchEvent(new Event('change',{ bubbles : true }))
        })
    }


    _getVariantById ( id ){
        return this.product.variants
            .find(( variant ) => variant.id === id )
    }


    _getVariantFromOptions (){

        const options = this._getSelectedOptionValues()

        return this.product.variants
            .find(( variant ) => variant.options
            .every(( value , index ) => value === options[ index ] ))
    }


    /**
     * Detect if a specific variant is selectable. This is used when the "hide sold out variant" option is enabled, to allow
     * to return true only if the variant is actually available
     */

    _isVariantSelectable ( variant ){

        if( ! variant )
            return false

        if( variant.available )
            return true

        if( this.hideSoldOutVariants )
            return false

        return true
    }


    /**
     * This method is used internally to select an available or selectable variant, when the current choice does not
     * match the requirements. For instance, if sold out variants are configured to be hidden, but that the choices end
     * up being a non-valid variant, the theme automatically changes the variant to match the requirements. In the case
     * the customer end up on variant combinations that do not exist, it also switches to a valid combination.
     *
     * The algorithm is as follows: if we have for instance three options "Color", "Size" and "Material", we pop the last
     * option (Material) and try to find the first available variant for the given Color and Size. If none is found we
     * remove the second option (Size) and try to find the first available variant for the selected color. Finally, if none
     * is found we return the first available variant independently of any choice.
     */

    _getFirstMatchingAvailableOrSelectableVariant (){

        const variants = this.product.variants

        const available = variants.filter(( variant ) =>
            variant.available || ! this.hideSoldOutVariants )


        const options = this._getSelectedOptionValues()


        let matchedVariant = null ,
            slicedCount = 0 ;

        while (
            ! matchedVariant &&
            options.length > 0
        ){

            options.pop()

            slicedCount += 1

            matchedVariant = available.find(( variant ) =>
                variant.options
                .filter(( option ) => option !== 'Default Title' )
                .slice(0,- slicedCount )
                .every(( value , index ) => value === options[ index ] )
            )
        }

        return matchedVariant
    }


    _getSelectedOptionValues (){

        const elements = this.querySelectorAll(`
            select[ name ^= option ] ,
            input[ name ^= option ]:checked
        `)

        return Array
            .from(elements)
            .map(( option ) => option.value )
    }


    /**
     * We add specific class to sold out variants based on the selectors
     */

    _updateDisableSelectors (){

        const { selectedVariant } = this

        if( ! selectedVariant )
            return

        const applyClassToSelector = (
            selector ,
            valueIndex ,
            available ,
            hasAtLeastOneCombination
        ) => {

            let selectorType = selector
                .getAttribute('data-selector-type')

            let cssSelector = "";


            switch ( selectorType ){
            case 'color' :
                cssSelector = `.color-swatch:nth-child(${ valueIndex + 1 })`
                break;
            case 'variant-image' :
                cssSelector = `.variant-swatch:nth-child(${ valueIndex + 1 })`
                break;
            case 'block' :
                cssSelector = `.block-swatch:nth-child(${ valueIndex + 1 })`
                break;
            case 'dropdown' :
                cssSelector = `.combo-box__option-item:nth-child(${ valueIndex + 1 })`
                break;
            }

            const element = selector.querySelector(cssSelector)

            if( ! element )
                return

            element.toggleAttribute('hidden', ! hasAtLeastOneCombination )

            if( this.hideSoldOutVariants)
                element.toggleAttribute('hidden', ! available )
            else
                element.classList.toggle('is-disabled', ! available )
        }


        if (this.optionSelectors && this.optionSelectors[0]) {
            this.product["options"][0]["values"].forEach((value, valueIndex) => {
              const hasAtLeastOneCombination = this.product["variants"].some((variant) => variant["option1"] === value && variant), hasAvailableVariant = this.product["variants"].some((variant) => variant["option1"] === value && variant["available"]);
              applyClassToSelector(this.optionSelectors[0], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);
              if (this.optionSelectors[1]) {
                this.product["options"][1]["values"].forEach((value2, valueIndex2) => {
                  const hasAtLeastOneCombination2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant), hasAvailableVariant2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant["available"]);
                  applyClassToSelector(this.optionSelectors[1], valueIndex2, hasAvailableVariant2, hasAtLeastOneCombination2);
                  if (this.optionSelectors[2]) {
                    this.product["options"][2]["values"].forEach((value3, valueIndex3) => {
                      const hasAtLeastOneCombination3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant), hasAvailableVariant3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant["available"]);
                      applyClassToSelector(this.optionSelectors[2], valueIndex3, hasAvailableVariant3, hasAtLeastOneCombination3);
                    });
                  }
                });
              }
            });
          }
    }
}


window.customElements.define('product-variants',ProductVariants)
